/**
 * A MEZŐJAVÍTÁS ŰRLAPJA (#42) — a fejléc HELYÉN nyílik.
 *
 * ⚠️ EZ AZ „A" IRÁNY, ÉS A VÁLASZTÁS INDOKA A TÉTELSOR. A vászon három
 * irányt vitt Viktor elé (`docs/tervezes/design/mezojavitas/`); a döntés
 * 2026-09-15-én az lett, hogy a javítás a fejléc helyére ül, mert így a
 * tételsorok NEM MOZDULNAK EL — a lefűzés ott folytatódik, ahol abbahagytad.
 * A „B" irány egy teljes mezőpanelt nyitott volna a fejléc alatt, és lecsúsztatta
 * volna a tételsort a hajtás alá; a „C" irány a mentés gombot vette volna el.
 *
 * Az ára, kimondva: a mezők SZŰKEK, és a származás csak a fókuszált mezőn fér
 * el. Ezért áll a „gép szerint …" a CÍMKE sorában, nem külön dobozban.
 *
 * ⚠️ NEM MODAL. A `CLAUDE.md` §3 nem-célja: „modal a bizonylat-feldolgozáshoz
 * — a modal egérre való, ez billentyűzetre". Az űrlap a lap szerkezetében ül,
 * a `Tab` végigmegy rajta, és nem csapdázza a fókuszt.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocType } from '@/i18n/generated';
import { isOutbound } from './docTypeChoice';
import type { CorrectableField, FieldView } from './fieldCorrection';
import { machineValueOf } from './fieldCorrection';
import { TypeChoice } from './TypeChoice';
import type { FieldCorrectionState } from './useFieldCorrection';

/**
 * A HOSSZÚ MEZŐK KÉT HASÁBOT KAPNAK. A partner neve és a bruttó az, amit a
 * könyvelő tényleg átír; a pénznem három betű, annak egy hasáb is sok.
 */
const WIDE: ReadonlySet<CorrectableField> = new Set(['partnerName', 'grossAmount']);

/** A pénz és a dátum monospace — ugyanaz a szabály, mint a lista sorain. */
const MONO: ReadonlySet<CorrectableField> = new Set([
  'partnerTaxNumber',
  'invoiceNumber',
  'issueDate',
  'deliveryDate',
  'periodMonth',
  'grossAmount',
  'currency',
]);

/**
 * MELYIK IRÁNYBA LÉP A NYÍL — `null`, ha a leütés a MEZŐÉ marad.
 *
 * Három feltétel, mind a három külön ok:
 *
 * ⚠️ CSAK A PUSZTA NYÍL LÉP MEZŐT. A `Ctrl+←` szóugrás, az `Alt+←` a böngésző
 * „vissza" gombja — a szöveg szélén mindkettő beleesne a mezőváltásba, és a
 * `preventDefault` elvenné a megszokott jelentésüket. A `Shift+nyíl` külön
 * nem kell: a kijelölés-feltétel amúgy is kiengedi.
 * *(Code review fogta meg, 09-18.)*
 *
 * A KIJELÖLT SZÖVEG NEM SZÉL. Kijelölés mellett a nyíl a kijelölést bontja
 * fel — az a böngésző dolga, nem a mezőváltásé.
 *
 * ÉS A KURZORNAK A SZÉLEN KELL ÁLLNIA: a szöveg végén (`→`) vagy elején
 * (`←`), ahol a böngészőnek sincs már dolga vele.
 */
function fieldStep(event: React.KeyboardEvent<HTMLInputElement>): -1 | 1 | null {
  if (event.altKey || event.ctrlKey || event.metaKey) {
    return null;
  }
  const input = event.currentTarget;
  if (input.selectionStart !== input.selectionEnd) {
    return null;
  }
  if (event.key === 'ArrowRight' && input.selectionStart === input.value.length) {
    return 1;
  }
  if (event.key === 'ArrowLeft' && input.selectionStart === 0) {
    return -1;
  }
  return null;
}

export function FieldCorrection({
  state,
  clientDocType = null,
}: {
  state: FieldCorrectionState;
  /** Amit az ügyfél mondott a típusról (#92) — a választó mellett áll. */
  clientDocType?: DocType | null;
}) {
  const { t } = useTranslation();

  /**
   * ⚠️ A FÓKUSZ AZ ELSŐ MEZŐRE MEGY, NEM A PANELRE. Billentyűzet-első
   * felületen a „megnyitottam, most hova nyúljak" pillanat a legdrágább —
   * a `Tab` innen már végigvisz.
   */
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const ready = state.fields !== null;
  useEffect(() => {
    if (ready) {
      inputs.current[0]?.focus();
      inputs.current[0]?.select();
    }
  }, [ready]);

  /**
   * A NYILAK IS VISZNEK MEZŐRŐL MEZŐRE (#16/3, Viktor 2026-09-14).
   *
   * ⚠️ CSAK A SZÖVEG SZÉLÉRŐL, ÉS EZ A LÉNYEG. A mezőben a `←` és a `→` a
   * KURZORT mozgatja — ez betűnként javított adószámnál nem kényelem, hanem
   * az egyetlen mód. Aki gépel, nem veszi észre; aki lépkedni akar, egy plusz
   * leütéssel átér. A feltételek a `fieldStep`-ben állnak, indoklással.
   *
   * ⚠️ ÉS NEM ROVING TABINDEX, pedig a #16 szövege azt írta elő. A roving
   * tabindex RÁCSRA való, ahol a cella nem szövegdoboz: itt egyetlen
   * `Tab`-megállóvá összevonva a `Tab`-bal lépkedő könyvelő és a
   * képernyőolvasó is ELVESZTENÉ a mezőnkénti bejárást — a felolvasó
   * mezőnként mondja be a címkét és a hibát, és azt a `Tab` váltja. A nyilak
   * MELLÉ kerülnek, nem HELYETTE. *(Ha Viktor mégis az egy-megállós alakot
   * kéri, az külön döntés — nem néma átírás.)*
   *
   * A KIJELÖLÉS A LÉPÉS UTÁN NEM ÁLL BE. A nyitáskori `select()` azt mondja:
   * „ezt írod felül"; egy lépkedés közben viszont a következő leütés TÖRÖLNÉ
   * a megnézett mező tartalmát.
   */
  function moveField(from: number, step: -1 | 1) {
    const next = inputs.current[from + step];
    if (!next) {
      return false;
    }
    next.focus();
    const caret = step === 1 ? 0 : next.value.length;
    next.setSelectionRange(caret, caret);
    return true;
  }

  function onFieldKeyDown(event: React.KeyboardEvent<HTMLInputElement>, index: number) {
    const step = fieldStep(event);
    if (step !== null && moveField(index, step)) {
      event.preventDefault();
    }
  }

  if (state.loading && state.fields === null) {
    return <output className="correction__loading">{t('worklist.correction.loading')}</output>;
  }

  if (state.fields === null || state.draft === null) {
    return null;
  }

  const fields = state.fields;
  // KIMENŐN A PARTNER ADÓSZÁMA NEM MINIMUM (#51) — a PISZKOZAT típusa szerint,
  // hogy a könyvelő a váltás pillanatában lássa, mit mozdít.
  const taxNumberOptional = state.docType !== null && isOutbound(state.docType);

  return (
    /* A `form` NEM kozmetika: ettől ment az `Enter` a beviteli mezőkből, és
       ettől jelenti be a képernyőolvasó, hogy űrlapon áll. */
    <form
      className="correction"
      aria-label={t('worklist.correction.heading')}
      onSubmit={(event) => {
        event.preventDefault();
        state.save();
      }}
      onKeyDown={(event) => {
        // Az `Esc` a mezőből is elvet — a könyvelőnek nem kell kikeresnie a
        // gombot ahhoz, hogy meggondolja magát.
        if (event.key === 'Escape') {
          event.preventDefault();
          state.cancel();
        }
      }}
    >
      <div className="correction__head">
        <span className="correction__heading">{t('worklist.correction.heading')}</span>
        <span className="correction__lead">{t('worklist.correction.lead')}</span>
        <span className="correction__hint">{t('worklist.correction.tabHint')}</span>
      </div>

      {/* A TÍPUS AZ ELSŐ SOR (#92, A irány). Előtte áll a DOM-ban, de a fókusz
          nyitáskor a mezőkre megy, mint eddig: a típusjavítás a ritkább eset,
          egy `Shift+Tab` visszavisz rá. */}
      {/* EGYETLEN LEHETŐSÉGNÉL NINCS MIT VÁLASZTANI — a választó el sem jön. */}
      {state.docType === null || state.typeOptions.length < 2 ? null : (
        <TypeChoice
          value={state.docType}
          options={state.typeOptions}
          clientDocType={clientDocType}
          onChange={state.changeType}
          onSubmit={state.save}
        />
      )}

      <div className="correction__grid">
        {fields.map((field, index) => (
          <Field
            key={field.fieldKey}
            field={field}
            value={state.draft?.[field.fieldKey] ?? ''}
            state={state}
            optional={field.fieldKey === 'partnerTaxNumber' && taxNumberOptional}
            registerInput={(element) => {
              inputs.current[index] = element;
            }}
            onKeyDown={(event) => onFieldKeyDown(event, index)}
          />
        ))}
      </div>

      {/* A MEZŐHÖZ NEM KÖTHETŐ HIBA a lábléc fölött áll — a mezőnkénti a
          mezője alatt (lásd a `Field`-et). */}
      {state.error !== null && state.error.fieldKey === null ? (
        <p className="correction__error" role="alert">
          {state.error.message}
        </p>
      ) : null}

      <div className="correction__foot">
        <button type="submit" disabled={state.saving}>
          {state.saving ? t('worklist.correction.saving') : t('worklist.correction.save')}
        </button>
        <span className="correction__hint">{t('worklist.correction.keyHint')}</span>
        <span className="correction__changed">
          {state.changedCount === 0
            ? t('worklist.correction.unchanged')
            : t('worklist.correction.changed', { count: state.changedCount })}
        </span>
      </div>
    </form>
  );
}

function Field({
  field,
  value,
  state,
  optional,
  registerInput,
  onKeyDown,
}: {
  field: FieldView;
  value: string;
  state: FieldCorrectionState;
  /** Nem minimum (kimenőn a partner adószáma) — a címke mondja ki. */
  optional: boolean;
  /** A SORREND A GRIDÉ, tehát a mezők sorrendjét a szülő tartja nyilván. */
  registerInput: (element: HTMLInputElement | null) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}) {
  const { t } = useTranslation();
  const label = t(`worklist.correction.label.${field.fieldKey}`);
  const machine = machineValueOf(field);
  const invalid = state.error?.fieldKey === field.fieldKey;
  const id = `correction-${field.fieldKey}`;
  const errorId = `${id}-error`;

  return (
    <div
      className={
        WIDE.has(field.fieldKey) ? 'correction__field correction__field--wide' : 'correction__field'
      }
    >
      <div className="correction__label-row">
        <label className="correction__label" htmlFor={id}>
          {label}
          {optional ? (
            <span className="correction__optional">
              {' '}
              · {t('worklist.correction.optionalOutbound')}
            </span>
          ) : null}
        </label>

        {/* A „JAVÍTOTTAD" JELVÉNY a tárolt állapotról szól, nem a begépeltről:
            azt mondja, hogy a szerveren MÁR áll egy kézi érték ezen a mezőn. */}
        {field.manualValue !== null ? (
          <span className="correction__badge">{t('worklist.correction.corrected')}</span>
        ) : null}

        {machine !== null ? (
          <>
            <span className="correction__machine">
              {/* A SZÁMOLT érték nem a gép olvasata, hanem a dátumokból jön (#74). */}
              {field.navValue === null && field.ocrValue === null
                ? t('worklist.correction.derivedValue', { value: machine })
                : t('worklist.correction.machineValue', { value: machine })}
            </span>
            {/* GOMB, NEM LINK: műveletet végez, nem navigál. */}
            <button
              type="button"
              className="correction__revert"
              aria-label={t('worklist.correction.revertFor', { field: label })}
              onClick={() => state.revert(field.fieldKey, machine)}
            >
              {t('worklist.correction.revert')}
            </button>
          </>
        ) : null}
      </div>

      <input
        id={id}
        ref={registerInput}
        onKeyDown={onKeyDown}
        className={MONO.has(field.fieldKey) ? 'correction__input num' : 'correction__input'}
        value={value}
        // ⚠️ `aria-invalid` ÉS `aria-describedby` EGYÜTT. Az első azt mondja,
        // hogy baj van; a második azt, hogy MI. Az egyik a másik nélkül a
        // képernyőolvasót ugyanoda juttatja, ahova a szín nélküli piros
        // keretet: „valami nem jó, találd ki".
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? errorId : undefined}
        onChange={(event) => state.change(field.fieldKey, event.target.value)}
      />

      {invalid ? (
        <p className="correction__error" id={errorId} role="alert">
          {state.error?.message}
        </p>
      ) : null}
    </div>
  );
}
